import { useEffect, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { LogOut, Heart, ShieldCheck } from "lucide-react";
import { Logo } from "@/components/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AreaPersonale = () => {
    const navigate = useNavigate();
    const location = useLocation();
    // null = checking, true = authenticated, false = not authenticated
    const [status, setStatus] = useState(location.state?.user ? true : null);
    const [user, setUser] = useState(location.state?.user || null);

    useEffect(() => {
        if (location.state?.user) return;
        axios
            .get(`${API}/auth/me`, { withCredentials: true })
            .then((res) => {
                setUser(res.data);
                setStatus(true);
            })
            .catch(() => setStatus(false));
    }, [location.state]);

    const logout = async () => {
        try {
            await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
        } catch (e) {
            // ignore network errors, go home anyway
        }
        navigate("/", { replace: true });
    };

    if (status === null) {
        return (
            <div
                data-testid="area-loading"
                className="min-h-screen bg-cream flex items-center justify-center"
            >
                <div className="flex flex-col items-center gap-4">
                    <span className="h-10 w-10 rounded-full border-[3px] border-line border-t-terracotta animate-spin" />
                    <p className="text-sm font-semibold text-mutedwarm">
                        Verifica della sessione…
                    </p>
                </div>
            </div>
        );
    }

    if (status === false) {
        return <Navigate to="/" replace />;
    }

    return (
        <div className="min-h-screen bg-cream text-ink overflow-x-clip">
            <div className="noise-overlay" aria-hidden="true" />
            <header className="glass-nav border-b border-line/70">
                <div className="mx-auto max-w-7xl px-5 lg:px-10 h-[110px] flex items-center justify-between">
                    <Logo />
                    <button
                        data-testid="area-home-link"
                        onClick={() => navigate("/")}
                        className="text-sm font-semibold text-mutedwarm hover:text-ink transition-colors duration-300"
                    >
                        Torna al sito
                    </button>
                </div>
            </header>

            <main className="mx-auto max-w-3xl px-5 lg:px-10 py-20 lg:py-28">
                <div
                    data-testid="area-page"
                    className="rounded-[28px] border border-line bg-stonewarm p-8 sm:p-12 text-center"
                >
                    <img
                        src={user.picture}
                        alt={user.name}
                        data-testid="area-avatar"
                        referrerPolicy="no-referrer"
                        className="h-20 w-20 rounded-full object-cover border-4 border-cream shadow-lg shadow-ink/10 mx-auto"
                    />
                    <p className="mt-6 text-xs font-bold uppercase tracking-[0.2em] text-terracotta">
                        Area personale
                    </p>
                    <h1
                        data-testid="area-name"
                        className="mt-3 font-serif text-4xl sm:text-5xl font-light tracking-tight"
                    >
                        Ciao, <em className="italic text-terracotta">{user.name}</em>!
                    </h1>
                    <p
                        data-testid="area-email"
                        className="mt-3 text-sm sm:text-base font-semibold text-mutedwarm"
                    >
                        {user.email}
                    </p>

                    <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-4 text-left">
                        <div className="rounded-2xl bg-cream border border-line p-5">
                            <Heart className="h-5 w-5 text-terracotta" />
                            <p className="mt-2 font-bold text-ink">
                                Sei dei nostri
                            </p>
                            <p className="text-sm text-mutedwarm">
                                La tua area personale è pronta: qui presto
                                troverai i tuoi appuntamenti e le tue routine.
                            </p>
                        </div>
                        <div className="rounded-2xl bg-cream border border-line p-5">
                            <ShieldCheck className="h-5 w-5 text-terracotta" />
                            <p className="mt-2 font-bold text-ink">
                                Sessione sicura
                            </p>
                            <p className="text-sm text-mutedwarm">
                                Accesso protetto con Google, valido per 7
                                giorni. Puoi uscire quando vuoi.
                            </p>
                        </div>
                    </div>

                    <button
                        data-testid="area-logout-button"
                        onClick={logout}
                        className="mt-10 inline-flex items-center gap-2 rounded-full border border-ink/20 px-7 py-3.5 text-sm font-bold text-ink transition-all duration-300 hover:bg-terracotta hover:text-cream hover:border-terracotta hover:-translate-y-0.5"
                    >
                        <LogOut className="h-4 w-4" />
                        Esci
                    </button>
                </div>
            </main>
        </div>
    );
};

export default AreaPersonale;
